/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/pro-regular-svg-icons";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { API } from "../../utils/AxiosInstance";
import { useClientId } from "../../contexts/branding";
import { Spinner } from "../components/spinner";
import { ApplicationState } from "../../reducers";
import { navigationMenuLabels } from "../../utils/configUtils";
import { ErrorText } from "../../components/error-text";
import "./feedback.scss";
import { mixpanelTrack } from "../../utils/mixpanel-utils";

const defaultCategory: Array<string> = [
    "Food",
    "Learn",
    "Discussions",
    "Health",
    "Videos",
    "Habits",
    "Device",
    "Other",
];

interface Props {
    onClose: () => void;
    onPopupClose: () => void;
    showEmail: boolean;
}

export default function FeedbackForm({
    onClose,
    onPopupClose,
    showEmail,
}: Props) {
    const { t } = useTranslation();
    const [loading, setLoadling] = useState<boolean>(false);
    const [file, setFile] = useState<any>("");
    const [error, setError] = useState("");
    const clientId = useClientId();

    const { nav_menu: navMenu } = useSelector(
        (state: ApplicationState) => state.appConfig
    );

    const menu = showEmail ? defaultCategory : navigationMenuLabels(navMenu);
    const menuList = [...menu, "Login/Registration"];

    const category = menuList.map((item) => ({ label: item, value: item }));
    const schema = yup.object().shape({
        subject: yup.string().trim().required("is required"),
        email: yup
            .string()
            .trim()
            .email("Invalid email")
            .required("is required"),
        feedback: yup.string().trim().required("is required"),
    });
    const noemailschema = yup.object().shape({
        subject: yup.string().trim().required("is required"),
        feedback: yup.string().trim().required("is required"),
    });
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(showEmail ? schema : noemailschema),
    });

    const validateSize = (f: File) => {
        const size = 15 * 1024 * 1024;
        let err = "";
        if (f && f.size > size) {
            err = `${f.type} ${t(
                "File is too large, please pick a smaller file"
            )}`;
        }
        if (!/\.(|jpg|jpeg|png|pdf)$/i.test(f.name)) {
            err = t("Please upload a valid file");
        }
        return err;
    };

    const handleFileUpload = (event: any) => {
        setError("");
        if (event.target.files) {
            const err = validateSize(event.target.files[0] as File);
            if (err) {
                setError(err);
            } else {
                setFile(event.target.files[0]);
            }
        } else {
            setError(t("Select a File to Upload"));
        }
    };

    const uploadAndSave = async (FormData: any) => {
        const config = {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        };
        if (showEmail) {
            await API.post(`/guestfeedbacks`, FormData, config)
                .then(() => {
                    onClose();
                    toast.success(t("feedback.saved_feedback"));
                })
                .catch((e) => {
                    setError((e as Error)?.message || "An error occurred");
                });
        } else {
            await API.post(`/feedbacks`, FormData, config)
                .then(() => {
                    onClose();
                    toast.success(t("feedback.saved_feedback"));
                })
                .catch((er) => {
                    setError((er as Error)?.message || "An error occurred");
                });
        }
    };

    const onSubmit = async (values: { [key: string]: string }) => {
        try {
            setLoadling(true);
            const formData = new FormData();
            formData.append("file", file as File);
            formData.append("email", values.email);
            formData.append("category", values.category);
            formData.append("subject", values.subject);
            formData.append("feedback", values.feedback);
            if (showEmail) {
                formData.append("client_id", clientId || "");
            }
            await uploadAndSave(formData);
        } catch {
            toast.error("feedback.could_not_save");
        }
        return true;
    };

    function handleClose() {
        onPopupClose();
    }
    return (
        <Modal show className="root feedback-modal-container">
            <Modal.Header>
                <Modal.Title>
                    <div className="login-header-text login-header-label">
                        {t("Contact us")}
                    </div>
                </Modal.Title>
                <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={handleClose}
                >
                    <span aria-hidden="true">×</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <div className="contact-us-container-inside">
                    <form
                        className="contact-us-form"
                        style={{ marginTop: 0 }}
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="login-label">{t("Category")}</div>
                        <select
                            className="form-control"
                            defaultValue=""
                            {...register("category", { required: true })}
                        >
                            <option value="">{t("Choose category")}</option>
                            {category.map(
                                (x: { label: string; value: string }) => (
                                    <option key={x.value} value={x.value}>
                                        {t(x.label)}
                                    </option>
                                )
                            )}
                        </select>

                        {errors.category && (
                            <ErrorText
                                text={
                                    typeof errors.category === "string"
                                        ? errors.category
                                        : (errors.category as any)?.message ||
                                          "Invalid input"
                                }
                            />
                        )}
                        <div className="login-label mt-2">
                            {t("Subject")}
                            <span style={{ color: "red" }}>*</span>
                        </div>
                        <input
                            defaultValue=""
                            {...register("subject", {})}
                            type="text"
                            className="form-control"
                            placeholder={t("feedback.subject_placeholder")}
                        />
                        {errors.subject && (
                            <ErrorText
                                text={
                                    typeof errors.subject === "string"
                                        ? errors.subject
                                        : (errors.subject as any)?.message ||
                                          "Invalid input"
                                }
                            />
                        )}

                        <div className="login-label mt-2">
                            {t("Message")}
                            <span style={{ color: "red" }}>*</span>
                        </div>
                        <textarea
                            defaultValue=""
                            {...register("feedback", {})}
                            className="form-control"
                            placeholder={t("Enter the message")}
                        />
                        {errors.feedback && (
                            <ErrorText
                                text={
                                    typeof errors.feedback === "string"
                                        ? errors.feedback
                                        : (errors.feedback as any)?.message ||
                                          "Invalid input"
                                }
                            />
                        )}

                        <div className="file-upload-section">
                            <div className="file-upload-input">
                                <label
                                    htmlFor="fileUpload"
                                    className="file-upload-link mt-2"
                                    style={{
                                        color: "#00087A",
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faUpload}
                                        color="#00087a"
                                    />
                                    {t("Add Files")}
                                </label>
                                <input
                                    id="fileUpload"
                                    type="file"
                                    className="custom-file-input"
                                    onChange={handleFileUpload}
                                />
                                {file && (
                                    <div className="file-name">
                                        {t("Uploaded File")}: {file.name}
                                    </div>
                                )}
                                {error && <ErrorText text={error} />}
                                <small className="form-text text-muted">
                                    {t(
                                        "Supported Formats: JPG, PNG, PDF, TXT, DOC"
                                    )}
                                    <br />
                                    {t("Maximum Size: 10 MB")}
                                </small>
                            </div>
                        </div>
                        <Row className="mt-3">
                            <div className="modal-footer">
                                <Col className="text-align-center">
                                    {loading ? (
                                        <Spinner />
                                    ) : (
                                        <Button
                                            variant="btn btn-success"
                                            type="submit"
                                            size="sm"
                                            className="btn-submit-feedback"
                                            onClick={() => {
                                                mixpanelTrack(
                                                    "Confirm Submit New Request Click",
                                                    {
                                                        step: "New Request Submit Button",
                                                        flow: "General Navigation",
                                                    }
                                                );
                                            }}
                                        >
                                            {t("Submit")}
                                        </Button>
                                    )}
                                </Col>
                            </div>
                        </Row>
                    </form>
                </div>
            </Modal.Body>
        </Modal>
    );
}
